import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { formatDate } from 'utils/date';
import { pendingBookingParamsToSync } from 'utils/url';

import { useAppOptions } from './use-app-options';
import { useAppointments } from './use-appointments';
import { useBooking } from './use-booking';
import { useDepartments } from './use-departments';
import { useMarkets } from './use-markets';
import { useProviders } from './use-providers';
import { useReasons } from './use-reasons';

/**
 * This hook syncs the booking state with the URL
 * It also sets the URL params and storage params in the booking state
 * Here is the flow:
 * 1. Gets the params from storage and URL, combines them
 * 2. Use the combined params to fill the initial booking state
 * 3. After all the params are fully synced with the booking state,
 *    any change in the booking state is synced back to URL
 */
const useSyncBookingWithUrl = (): void => {
    const { state } = useLocation();
    const appOptions = useAppOptions('main');
    const { data: markets } = useMarkets();
    const { booking, setBookingField } = useBooking();
    const { data: reasons } = useReasons(booking);
    const { data: providers } = useProviders(booking);
    const { data: departments } = useDepartments(booking);
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const [_searchParams, setSearchParams] = useSearchParams();

    // fetching appointment here is only used to sync the appointmentId from the url to booing state
    // After the appointment is synced we disable the hook to prevent unwanted issues
    const appointmentPendingToSync = !booking.appointment && pendingBookingParamsToSync.has('appointmentId');
    const { data: appointments } = useAppointments(booking, providers, { enabled: appointmentPendingToSync });

    useEffect(() => {
        setSearchParams(
            (previousSearch) => {
                const newSearchParams = new URLSearchParams(previousSearch);
                const { market, isExistingPatient, department, reason, appointment, provider, date } = booking;

                if (market) {
                    newSearchParams.set('marketKey', market.market_key);
                }

                if (isExistingPatient !== undefined) {
                    newSearchParams.set('isExistingPatient', isExistingPatient.toString());
                }

                if (department) {
                    newSearchParams.set('departmentId', department.departmentid.toString());
                } else if (!pendingBookingParamsToSync.has('departmentId')) {
                    newSearchParams.delete('departmentId');
                }

                if (reason) {
                    newSearchParams.set('reasonId', reason.id.toString());
                } else if (!pendingBookingParamsToSync.has('reasonId')) {
                    newSearchParams.delete('reasonId');
                }

                if (provider) {
                    newSearchParams.set('providerId', provider.providerid.toString());
                } else if (!pendingBookingParamsToSync.has('providerId')) {
                    newSearchParams.delete('providerId');
                }

                if (date) {
                    newSearchParams.set('date', formatDate(date, 'iso'));
                }

                if (appointment) {
                    newSearchParams.set('appointmentId', appointment.appointmentid.toString());
                } else if (!pendingBookingParamsToSync.has('appointmentId')) {
                    newSearchParams.delete('appointmentId');
                }

                if (previousSearch.toString() !== newSearchParams.toString()) {
                    return newSearchParams;
                }

                return previousSearch;
            },
            { replace: true, state }
        );
    }, [booking, state]);

    useEffect(() => {
        if (pendingBookingParamsToSync.size === 0) {
            return;
        }

        const { market, isExistingPatient, department, reason, provider } = booking;

        const urlMarket = appOptions.marketKey
            ? markets?.find(({ market_key }) => market_key === appOptions.marketKey)
            : undefined;

        if (!market && markets && pendingBookingParamsToSync.has('marketKey')) {
            pendingBookingParamsToSync.delete('marketKey');
            setBookingField({ market: urlMarket });
        }

        if (
            isExistingPatient === undefined &&
            appOptions.isExistingPatient !== undefined &&
            pendingBookingParamsToSync.has('isExistingPatient')
        ) {
            pendingBookingParamsToSync.delete('isExistingPatient');
            setBookingField({ isExistingPatient: appOptions.isExistingPatient });
        }

        const urlDepartment = appOptions.departmentId
            ? departments?.find(({ departmentid }) => departmentid === Number(appOptions.departmentId))
            : undefined;

        if (!department && departments && pendingBookingParamsToSync.has('departmentId')) {
            pendingBookingParamsToSync.delete('departmentId');
            setBookingField({ department: urlDepartment });
        }

        const urlReason = appOptions.reasonId ? reasons?.find(({ id }) => id === Number(appOptions.reasonId)) : undefined;

        if (!reason && reasons && pendingBookingParamsToSync.has('reasonId')) {
            pendingBookingParamsToSync.delete('reasonId');
            setBookingField({ reason: urlReason });
        }

        const urlProvider = appOptions.providerId
            ? providers?.find(({ providerid }) => Number(appOptions.providerId) === providerid)
            : undefined;

        if (!provider && providers.length > 0 && pendingBookingParamsToSync.has('providerId')) {
            pendingBookingParamsToSync.delete('providerId');
            setBookingField({ provider: urlProvider });
        }

        if (appOptions.date && pendingBookingParamsToSync.has('date')) {
            pendingBookingParamsToSync.delete('date');
            setBookingField({ date: appOptions.date });
        }

        const urlAppointment = appOptions.appointmentId
            ? appointments?.find(({ appointmentid }) => appointmentid === Number(appOptions.appointmentId))
            : undefined;

        if (appointmentPendingToSync && appointments) {
            pendingBookingParamsToSync.delete('appointmentId');
            setBookingField({ appointment: urlAppointment });
        }
    }, [appOptions, markets, departments, reasons, providers, appointments, setBookingField, booking, appointmentPendingToSync]);
};

export { useSyncBookingWithUrl };
